import React, {useState, useEffect} from 'react';

import {useMutation, useSubscription} from '@apollo/client/react';
import {X, Building2, AlertCircle} from 'lucide-react';
import {gql} from 'types/__generated__';
import type {OrganisationQuery} from 'types/__generated__/graphql';

const UPDATE_ORGANISATION = gql(`
  mutation UpdateOrganisationBaseInformation($organisationId: UUID!, $name: String!, $description: String) {
    updateOrganisationBaseInformation(organisationId: $organisationId, name: $name, description: $description)
  }
`);

const ORGANISATION_SUBSCRIPTION = gql(`
  subscription OrganisationUpdatedForEdit($id: UUID!) {
    organisation(id: $id) {
      id
      name
      description
      active
    }
  }
`);

/** The organisation as the Organisation query selects it. */
type EditableOrganisation = NonNullable<OrganisationQuery['organisation']>;

interface EditOrganisationModalProps {
  organisation: EditableOrganisation;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function EditOrganisationModal({organisation, isOpen, onClose, onSuccess}: EditOrganisationModalProps) {
  const [name, setName] = useState(organisation.name || '');
  const [description, setDescription] = useState(organisation.description || '');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pendingUpdate, setPendingUpdate] = useState(false);

  // Reset form when modal opens with new data
  useEffect(() => {
    if (isOpen) {
      setName(organisation.name || '');
      setDescription(organisation.description || '');
      setError('');
      setSuccess('');
      setPendingUpdate(false);
    }
  }, [isOpen, organisation]);

  // Subscribe to organisation updates
  const {data: subscriptionData} = useSubscription(ORGANISATION_SUBSCRIPTION, {
    variables: {id: organisation.id},
    skip: !isOpen,
  });

  // Update form fields when subscription receives updates
  useEffect(() => {
    if (subscriptionData?.organisation) {
      const updated = subscriptionData.organisation;
      console.log('📨 Organisation subscription update received:', updated);

      setName(updated.name || '');
      setDescription(updated.description || '');

      if (pendingUpdate) {
        console.log('✅ Update confirmed via subscription, closing modal');
        setPendingUpdate(false);
        setSuccess('Updated successfully!');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 500);
      }
    }
  }, [subscriptionData, pendingUpdate, onClose, onSuccess]);

  const [updateOrganisation, {loading}] = useMutation(UPDATE_ORGANISATION);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!name.trim()) {
      setError('Organisation name is required');
      return;
    }

    try {
      await updateOrganisation({
        variables: {
          organisationId: organisation.id,
          name: name.trim(),
          description: description.trim() || null,
        },
      });
      setPendingUpdate(true);
      setSuccess('Updating organisation...');
    } catch (err: any) {
      setError(err.message || 'Failed to update organisation');
      console.error('Failed to update organisation:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose} />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                <Building2 size={20} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Edit Organisation</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400">Update organisation information</p>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
              <X size={20} className="text-gray-600 dark:text-gray-400" />
            </button>
          </div>

          {/* Content */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {error && (
              <div className="flex items-center space-x-2 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <AlertCircle size={20} className="text-red-600 dark:text-red-400" />
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            {success && (
              <div className="flex items-center space-x-2 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <AlertCircle size={20} className="text-green-600 dark:text-green-400" />
                <p className="text-sm text-green-600 dark:text-green-400">{success}</p>
              </div>
            )}

            {/* Organisation Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Organisation Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter organisation name"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter organisation description (optional)"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                disabled={loading || pendingUpdate}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading || pendingUpdate}
              >
                {loading || pendingUpdate ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
